import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

interface Crumb {
  label: string
  to?: string
}

interface Props {
  eyebrow: string
  title: string
  intro?: string
  image: string
  crumbs?: Crumb[]
  gold?: boolean
  height?: 'default' | 'tall'
}

/**
 * Full-bleed hero banner used at the top of list/detail pages.
 * Cosmos FC real image + gradient overlay + eyebrow / title / intro / breadcrumbs.
 */
export function PageHero({ eyebrow, title, intro, image, crumbs, gold, height = 'default' }: Props) {
  return (
    <section
      className={cn(
        'relative overflow-hidden text-white',
        height === 'tall' ? 'min-h-[520px]' : 'min-h-[380px]'
      )}
    >
      <img
        src={image}
        alt=""
        loading="eager"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-pitch-950/95 via-pitch-900/80 to-pitch-800/60" />
      <div className="absolute inset-0 [background:repeating-linear-gradient(90deg,rgba(255,255,255,0.04)_0_60px,rgba(0,0,0,0.06)_60px_120px)]" />

      <div className="container-narrow relative z-10 pt-32 pb-16 flex flex-col justify-end min-h-inherit">
        {crumbs && (
          <nav className="flex items-center gap-1.5 text-xs text-white/70 mb-6">
            <Link to="/" className="hover:text-cosmos-green">Home</Link>
            {crumbs.map((c) => (
              <span key={c.label} className="flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 opacity-60" />
                {c.to ? (
                  <Link to={c.to} className="hover:text-cosmos-green">{c.label}</Link>
                ) : (
                  <span className="text-white/90">{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn('eyebrow', gold ? 'eyebrow-gold' : '')}
          style={{ color: gold ? '#F5C518' : '#4ADE80' }}
        >
          {eyebrow}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 font-display uppercase leading-none text-5xl md:text-7xl"
        >
          {title}
        </motion.h1>
        {intro && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-5 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed"
          >
            {intro}
          </motion.p>
        )}
      </div>
    </section>
  )
}
